// utils/templateValidator.js
// === STEP 1: SIMPLIFIED VALIDATOR ===
//
// What this validator does now:
//   - Accepts a caseType derived from (data shape + user's screen choice)
//   - Checks ONLY the loop-budget for that case:
//       • case2 (single screen)  → max 1 loop
//       • case1 / case3 (multi)  → max 2 loops
//       • unclassified           → error, no valid response
//   - NEVER complains about "no loops" — zero loops is always valid.

export class TemplateValidator {
  // ============================================================
  // MAIN ENTRY POINT
  // ============================================================
  static validateForCase(template, caseType) {
    const loopInfo = this.analyzeLoops(template);
    const errors = [];
    const warnings = []; // kept for API compat; always empty

    // ------------------------------------------------------------
    // UNCLASSIFIED — the only "can't determine" state
    // (empty response, invalid JSON, or null)
    // ------------------------------------------------------------
    if (caseType === 'unclassified') {
      errors.push({
        code: 'NO_VALID_RESPONSE',
        title: '📋 No Valid Response',
        message: `We can't determine a layout because the response is empty, null, or not valid JSON.`,
        context: `The response must be a valid JSON value — string, number, boolean, object, or array.`,
        details: null,
        solution: `Paste a valid JSON response into the Sample Response field to continue.`,
        fix: `Try pasting something like: {"name": "John"} or [{"id": 1}]`,
        visual: null,
        severity: 'error',
      });
      return {
        valid: false,
        errors,
        warnings,
        loopInfo,
        caseType,
        summary: this.getSummary(loopInfo, caseType),
      };
    }

    // ------------------------------------------------------------
    // CASE 2 — Single screen → max 1 loop
    // ------------------------------------------------------------
    if (caseType === 'case2') {
      if (loopInfo.maxDepth > 1) {
        errors.push({
          code: 'SINGLE_SCREEN_TOO_MANY_LOOPS',
          title: '📐 Single Screen Supports Only 1 Loop',
          message: `Your template has nested loops, but a single-screen layout allows only 1 loop.`,
          context: `On a single screen, all content appears on ONE page. You can loop over one array to repeat content, but you cannot nest another loop inside it.`,
          details: `You have ${loopInfo.maxDepth} levels of loops. Single-screen layouts support at most 1.`,
          solution: `If you want one screen per item, switch to Multiple Screens. Otherwise, reduce your template to a single loop.`,
          fix: `Remove the inner loop, OR go back and choose "Multiple Screens" for this response.`,
          visual: `
            ✅ CORRECT (Single Screen — 1 loop):
            Room: {{ response_data.room }}
            {% for booking in response_data.bookings %}
              Guest: {{ booking.guest }}
            {% endfor %}
            
            ❌ INCORRECT (nested loops):
            {% for booking in response_data.bookings %}
              {% for guest in booking.guests %}
                Guest: {{ guest.name }}
              {% endfor %}
            {% endfor %}
          `,
          severity: 'error',
        });
      }
    }

    // ------------------------------------------------------------
    // CASE 1 / CASE 3 — Multiple screens → max 2 loops
    // ------------------------------------------------------------
    if (caseType === 'case1' || caseType === 'case3') {
      if (loopInfo.maxDepth > 2) {
        errors.push({
          code: 'MULTIPLE_SCREENS_TOO_MANY_LOOPS',
          title: '📚 Multiple Screens Support At Most 2 Loops',
          message: `Your template has too many levels of nested loops.`,
          context: `In a multi-screen layout, the OUTER loop creates screens (one per item). Then you can have ONE inner loop for content within each screen. Going deeper than 2 levels is not supported.`,
          details: `You have ${loopInfo.maxDepth} levels of loops. Multi-screen layouts support at most 2.`,
          solution: `Reduce nesting to 2 levels. If your data has 3+ levels, restructure the response so the deepest level is a flat array.`,
          fix: `Remove the innermost loop, or restructure the response.`,
          visual: `
            ✅ CORRECT (2 Levels):
            {% for item in response_data %}       ← Level 1 (screens)
              Item: {{ item.name }}
              {% for tag in item.tags %}          ← Level 2 (content)
                Tag: {{ tag }}
              {% endfor %}
            {% endfor %}
            
            ❌ INCORRECT (3+ Levels):
            {% for item in response_data %}       ← Level 1
              {% for tag in item.tags %}          ← Level 2
                {% for detail in tag.details %}  ← Level 3 (NOT SUPPORTED)
                  {{ detail }}
                {% endfor %}
              {% endfor %}
            {% endfor %}
          `,
          severity: 'error',
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      loopInfo,
      caseType,
      summary: this.getSummary(loopInfo, caseType),
    };
  }

  // ============================================================
  // ANALYZE LOOPS — unchanged, used only to count depth
  // ============================================================
  static analyzeLoops(template) {
    if (!template || typeof template !== 'string') {
      return {
        maxDepth: 0,
        maxNestedLoops: 0,
        loops: [],
        hasNestedLoops: false,
        totalLoops: 0,
        emptyLoops: 0,
        loopDetails: [],
      };
    }

    const lines = template.split('\n');
    let maxDepth = 0;
    let currentDepth = 0;
    let nestedLoopCount = 0;
    let maxNestedLoops = 0;
    const loops = [];

    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();

      if (/{%\s*for\s+/.test(trimmed)) {
        const isNested = currentDepth > 0;
        if (isNested) {
          nestedLoopCount++;
          maxNestedLoops = Math.max(maxNestedLoops, nestedLoopCount);
        }
        currentDepth++;
        maxDepth = Math.max(maxDepth, currentDepth);

        const match = trimmed.match(
          /{%\s*for\s+(\w+)\s+in\s+([\w.]+)\s*%}/
        );
        loops.push({
          line: trimmed,
          lineNumber: i,
          depth: currentDepth,
          isNested,
          variable: match ? match[1] : 'item',
          iterable: match ? match[2] : 'unknown',
        });
      }

      if (/{%\s*endfor\s*%}/.test(trimmed)) {
        currentDepth--;
        if (loops.length > 0) {
          const lastLoop = loops[loops.length - 1];
          if (
            lastLoop &&
            lastLoop.depth > 1 &&
            lastLoop.depth === currentDepth + 1
          ) {
            nestedLoopCount--;
          }
        }
      }
    }

    return {
      maxDepth,
      maxNestedLoops,
      loops,
      hasNestedLoops: maxDepth > 1,
      totalLoops: loops.length,
      emptyLoops: 0,
      loopDetails: loops.map((l) => ({
        variable: l.variable,
        iterable: l.iterable,
        depth: l.depth,
        isNested: l.isNested,
        lineNumber: l.lineNumber + 1,
        sampleLine: l.line,
      })),
    };
  }

  // ============================================================
  // CASE DESCRIPTIONS — updated to reflect data-driven model
  // ============================================================
  static getCaseDescription(caseType) {
    switch (caseType) {
      case 'case1':
        return {
          name: 'Multiple Screens (Root Array)',
          description:
            'Your response is an array at the root. Each item renders on its own screen.',
          maxLoopDepth: 2,
          structure: 'Outer loop (creates screens) + optional inner loop',
          example: `{% for item in response_data %}\n  {{ item.name }}\n  {% for tag in item.tags %}\n    {{ tag }}\n  {% endfor %}\n{% endfor %}`,
        };
      case 'case2':
        return {
          name: 'Single Screen',
          description:
            'All content appears on one screen. At most 1 loop is allowed.',
          maxLoopDepth: 1,
          structure: 'Optional single loop (no nesting)',
          example: `Room: {{ response_data.room }}\n{% for booking in response_data.bookings %}\n  Guest: {{ booking.guest }}\n{% endfor %}`,
        };
      case 'case3':
        return {
          name: 'Multiple Screens (Chosen Outer Array)',
          description:
            'A chosen array on your response creates one screen per item. One inner loop is allowed per screen.',
          maxLoopDepth: 2,
          structure: 'Outer loop (creates screens) + optional inner loop',
          example: `{% for booking in response_data.bookings %}\n  {{ booking.check_in }}\n  {% for guest in booking.guests %}\n    {{ guest.name }}\n  {% endfor %}\n{% endfor %}`,
        };
      case 'unclassified':
        return {
          name: 'Unclassified',
          description:
            'The response is empty, null, or not valid JSON — layout cannot be determined.',
          maxLoopDepth: 0,
          structure: 'Unknown',
          example: '',
        };
      default:
        return {
          name: 'Unknown',
          description: 'Unable to determine the layout.',
          maxLoopDepth: 2,
          structure: 'Unknown',
          example: '',
        };
    }
  }

  static getSummary(loopInfo, caseType) {
    const caseDesc = this.getCaseDescription(caseType);
    return {
      layoutType: caseDesc.name,
      maxLoopDepth: loopInfo.maxDepth,
      totalLoops: loopInfo.totalLoops,
      hasNestedLoops: loopInfo.hasNestedLoops,
      recommendedMaxDepth: caseDesc.maxLoopDepth,
      loopDetails: loopInfo.loopDetails,
      exampleTemplate: caseDesc.example,
    };
  }

  // ============================================================
  // USER-FRIENDLY ERROR MAPPER
  // ============================================================
  static getUserFriendlyError(error, loopInfo, caseType) {
    const errorMap = {
      NO_VALID_RESPONSE: {
        title: '📋 No Valid Response',
        message: `We can't determine a layout because the response is empty, null, or not valid JSON.`,
        context: `The response must be a valid JSON value.`,
        details: null,
        solution: `Paste a valid JSON response to continue.`,
        fix: `Try: {"name": "John"} or [{"id": 1}]`,
        learnMore: `💡 The response must be a valid JSON value: string, number, boolean, object, or array.`,
      },
      SINGLE_SCREEN_TOO_MANY_LOOPS: {
        title: '📐 Single Screen Supports Only 1 Loop',
        message: `Your template has nested loops, but a single-screen layout allows only 1 loop.`,
        context: `On a single screen, all content appears on ONE page. You can loop over one array, but you cannot nest a loop inside it.`,
        details: `You have ${loopInfo?.maxDepth || 0} levels of loops. Single-screen allows at most 1.`,
        solution: `Switch to Multiple Screens, or reduce your template to a single loop.`,
        fix: `Remove the inner loop, or go back and choose "Multiple Screens" for this response.`,
        learnMore: `💡 Single Screen → 1 loop max\nMultiple Screens → 2 loops max (outer + inner)`,
      },
      MULTIPLE_SCREENS_TOO_MANY_LOOPS: {
        title: '📚 Multiple Screens Support At Most 2 Loops',
        message: `Your template has too many levels of nested loops.`,
        context: `Multi-screen layouts allow one OUTER loop (creates screens) and ONE inner loop (content per screen).`,
        details: `You have ${loopInfo?.maxDepth || 0} levels of loops. Multi-screen allows at most 2.`,
        solution: `Reduce nesting to 2 levels, or restructure your response.`,
        fix: `Remove the innermost loop, or restructure the response.`,
        learnMore: `💡 Common structures:\n• {% for item in data %} → screens\n• {% for sub in item.children %} → content\n• Deeper than that → not supported`,
      },
    };

    return errorMap[error.code] || {
      title: '⚠️ Template Error',
      message: error.message,
      context: error.context || 'Your template structure needs attention.',
      solution: error.fix || 'Please review your template structure.',
      fix: error.fix || 'Check your loop structure and try again.',
      learnMore: '💡 Click "Open Editor" to inspect your template.',
    };
  }
}